import { Table } from "@/components/application/table/table";
import { ProgressBarBase } from "@/components/base/progress-indicators/progress-indicators";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";

/**
 * Shared table cell components used across the Creators / Candidates / Best
 * Matches tables so the unified column layout stays visually identical.
 */

type MatchQualityCellProps = {
  score: number | null | undefined;
};

/**
 * Skeleton shown while the lazy /metrics fetch is in-flight.
 * Matches the shimmer style used in the target-collaborations table (UNB-1371).
 */
const MetricSkeleton = () => <span className="inline-block h-3 w-12 rounded bg-quaternary/30 animate-pulse" />;

// Bare content, no `<Table.Cell>` wrapper — for callers (like UnboxTable columns)
// that supply their own cell wrapper.
export const MatchQualityCellContent = ({ score }: MatchQualityCellProps) => {
  if (score === undefined) return <MetricSkeleton />;
  if (score === null) return "-";
  return (
    <Tooltip
      title="This creator's match score combines two factors"
      description={
        <div className="flex flex-col gap-3 text-left">
          <div>
            <div className="font-medium mb-1">Revenue Potential</div>
            <ul className="list-disc list-inside text-xs">
              <li>
                Our AI analyses all creators and brand features (posting frequency, historical performance, audience
                demographics, etc.) to predict how much revenue they could generate for your brand.
              </li>
            </ul>
          </div>
          <div>
            <div className="font-medium mb-1">Brand Fit</div>
            <ul className="list-disc list-inside text-xs">
              <li>
                We're comparing your brand vertical with the creators primary content category to identify alignment.
              </li>
            </ul>
          </div>
        </div>
      }
    >
      <TooltipTrigger>
        <div className="flex flex-row gap-2 items-center cursor-help">
          <span>{score}%</span>
          <ProgressBarBase value={score} className="w-20 h-1.5" progressClassName="bg-brand-900" />
        </div>
      </TooltipTrigger>
    </Tooltip>
  );
};

export const MatchQualityCell = (props: MatchQualityCellProps) => (
  <Table.Cell className="whitespace-nowrap text-xs text-primary font-medium px-2">
    <MatchQualityCellContent {...props} />
  </Table.Cell>
);

type GenderDemoCellProps = {
  genderDistribution: { female: number; male: number } | null | undefined;
};

// Bare content, no `<Table.Cell>` wrapper — for callers (like UnboxTable columns)
// that supply their own cell wrapper.
export const GenderDemoCellContent = ({ genderDistribution }: GenderDemoCellProps) => {
  const femaleIsDominant = genderDistribution ? genderDistribution.female >= genderDistribution.male : true;
  if (!genderDistribution) return <span className="text-xs text-primary font-medium">-</span>;
  return (
    <div className="flex flex-col gap-1 max-w-[120px]">
      <div className="relative h-2 w-full overflow-hidden rounded-full bg-gray-200">
        <div
          className={`absolute inset-y-0 rounded-full bg-gray-500 ${femaleIsDominant ? "left-0" : "right-0"}`}
          style={{ width: `${femaleIsDominant ? genderDistribution.female : genderDistribution.male}%` }}
        />
      </div>
      <div className="flex justify-between text-[11px] text-tertiary whitespace-nowrap">
        <span>{genderDistribution.female}% F</span>
        <span>{genderDistribution.male}% M</span>
      </div>
    </div>
  );
};

export const GenderDemoCell = (props: GenderDemoCellProps) => (
  <Table.Cell className="px-2">
    <GenderDemoCellContent {...props} />
  </Table.Cell>
);

type MainAgeDemoCellProps = {
  topAgeRange: { range: string; percentage: number } | null | undefined;
};

// Bare content, no `<Table.Cell>` wrapper — for callers (like UnboxTable columns)
// that supply their own cell wrapper.
export const MainAgeDemoCellContent = ({ topAgeRange }: MainAgeDemoCellProps) => (
  <>
    {topAgeRange ? `${topAgeRange.range}` : "-"}{" "}
    {topAgeRange ? <span className="text-[12px] text-quaternary">{`(${topAgeRange.percentage}%)`}</span> : ""}
  </>
);

export const MainAgeDemoCell = (props: MainAgeDemoCellProps) => (
  <Table.Cell className="whitespace-nowrap text-sm text-tertiary font-medium px-2">
    <MainAgeDemoCellContent {...props} />
  </Table.Cell>
);

type TrendNumberCellProps = {
  value: number | null | undefined;
  trend: number | null | undefined;
  format: (n: number) => string;
};

/**
 * Renders a numeric value with a percent-change trend badge (green / red),
 * matching the GMV and Videos columns in the Best Matches table.
 */
// Bare content, no `<Table.Cell>` wrapper — for callers (like UnboxTable columns)
// that supply their own cell wrapper.
export const TrendNumberCellContent = ({ value, trend, format }: TrendNumberCellProps) => {
  const change = trend != null && trend > 0 && value != null ? (value - trend) / trend : null;

  return (
    <div className="flex flex-row gap-1 items-center">
      {value != null ? format(value) : "-"}
      {change !== null && <TrendPercent value={change} />}
    </div>
  );
};

export const TrendNumberCell = (props: TrendNumberCellProps) => (
  <Table.Cell className="whitespace-nowrap text-xs text-primary font-medium px-2">
    <TrendNumberCellContent {...props} />
  </Table.Cell>
);

const percentFormatter = new Intl.NumberFormat("en-US", {
  style: "percent",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
  signDisplay: "exceptZero",
});

const TrendPercent = ({ value }: { value: number }) => (
  <span className={value >= 0 ? "text-success-600" : "text-error-600"}>{percentFormatter.format(value)}</span>
);
